import { AutoAwesomeOutlined, PhotoLibraryOutlined } from '@mui/icons-material'
import { FC, useEffect, useRef } from 'react'

import { BottomBar } from 'components/common/BottomBar'
import { DEFAULT_SOCIAL_NETWORKS } from 'models/socialCampaign'
import { GRAY_800 } from 'constants/styling/theme'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { NetworkMark } from './NetworkMark.component'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import { VisualEmptyState } from 'components/common/VisualEmptyState'
import { createPortal } from 'react-dom'
import styles from './CampaignIntro.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Visuals the property can feed into a campaign; the chip waits for the inventory */
  visualsReady: number | null
  /** A photo of the property for the picture on the right */
  photoUrl: string | null
  /** The last creation failed: an alert with a retry above the intro */
  failed: boolean
  onStart: () => void
}

/**
 * No campaign yet (prototype v3 `introHtml`): the two-column visual empty state, the networks a new
 * campaign publishes to, facts from the inventory, and the start, in the page from the `sm` breakpoint and in a bottom bar on a
 * phone. The bottom bar is portalled to <body>: the property page's GSAP transitions leave a transform on
 * its ancestors, which would turn `position: fixed` into a scrolling one.
 */
export const CampaignIntro: FC<Props> = ({ visualsReady, photoUrl, failed, onStart }) => {
  const { t } = useTranslation(['social_campaign'])
  const retryRef = useRef<HTMLButtonElement>(null)

  // back from a failed generation, whose title held the focus: the retry takes it
  useEffect(() => {
    if (failed) retryRef.current?.focus()
  }, [failed])

  return (
    <>

      {failed &&
        <SystemMessage
          variant="error"
          role="alert"
          title={t('intro.error_title')}
          message={t('intro.error_body')}
          isRawContent
          actions={<MUIButton ref={retryRef} type="secondaryBorder" size="sm" onClick={onStart}>{t('intro.retry')}</MUIButton>}
        />
      }

      <VisualEmptyState
        className={styles.intro}
        title={t('intro.title')}
        description={t('intro.body')}
        label={t('intro.publishes_to')}
        labelContent={
          <ul className={styles.networks}>
            {DEFAULT_SOCIAL_NETWORKS.map((network) => (
              <li key={network} className={styles.network}>
                <NetworkMark network={network} size={20} />
                <Typography variant="text-sm" color={GRAY_800}>{t(`network.${network}`)}</Typography>
              </li>
            ))}
          </ul>
        }
        chips={
          <>
            {visualsReady !== null && <MUIBadge color="white" size="lg" label={t('intro.visuals_ready', { count: visualsReady })} />}
            <MUIBadge color="white" size="lg" label={t('intro.one_text')} />
            <MUIBadge color="white" size="lg" label={t('intro.pause_any_time')} />
          </>
        }
        actions={<MUIButton size="lg" startIcon={<AutoAwesomeOutlined />} onClick={onStart}>{t('intro.start')}</MUIButton>}
        isActionsHiddenOnPhone
        media={photoUrl
          ? <img src={photoUrl} alt="" />
          : <span className={styles.placeholder} aria-hidden="true"><PhotoLibraryOutlined sx={{ fontSize: '4.8rem' }} /></span>
        }
      />

      {typeof document !== 'undefined' && createPortal(
        <BottomBar
          isFixed
          isPhoneOnly
          aria-label={t('intro.start')}
          actions={<MUIButton size="lg" startIcon={<AutoAwesomeOutlined />} onClick={onStart}>{t('intro.start')}</MUIButton>}
        />,
        document.body
      )}

    </>
  )
}
